import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from '../hooks/use-toast';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';

const practiceAreasList = [
  'Civil Litigation',
  'Criminal Law',
  'Writ Petitions',
  'Bail Matters',
  'Matrimonial & Family',
  'FIR & Cognizance Quashing',
  'Cheque Bounce NI Act',
  'Property & Land Disputes',
  'Consumer Protection',
  'Corporate & Commercial',
  'Banking & NBFC Disputes',
  'Arbitration & Mediation',
  'Service Matters',
  'DRT Cases',
  'NCLT / NCLAT',
  'PIL',
  'High Court & Supreme Court',
  'Legal Drafting',
  'Railway Claims'
];

const ContactSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    matterType: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: "Thank you!",
      description: "We will contact you shortly.",
    });
    setFormData({
      fullName: '',
      phone: '',
      email: '',
      matterType: '',
      description: ''
    });
  };

  return (
    <section id="contact" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ color: '#1A1A1A' }}
          >
            Get in Touch
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Left Column - Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#2B2BFF' }} />
                <div>
                  <p className="text-base leading-relaxed" style={{ color: '#555555' }}>
                    New Bhupatipur, Near Sanskriti Marriage Garden,<br />
                    Patna, Bihar – 800020
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Phone className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#2B2BFF' }} />
                <div>
                  <p className="text-base" style={{ color: '#555555' }}>
                    +91 79039 27994 | +91 74929 06530
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Mail className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#2B2BFF' }} />
                <div>
                  <p className="text-base" style={{ color: '#555555' }}>
                    officenamorisatyalegal@gmail.com
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="h-6 w-6 flex-shrink-0 mt-1" style={{ color: '#2B2BFF' }} />
                <div>
                  <p className="text-base" style={{ color: '#555555' }}>
                    Office Hours: 5:00 PM – 10:00 PM (Mon–Sat)
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.8709613859014!2d85.13275589999999!3d25.5759595!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f2a75a809dab1f%3A0x7ee20cb373bc00b!2sNAMORISATYA%20LEGAL%20%26%20ASSOCIATES!5e0!3m2!1sen!2sin!4v1773904239531!5m2!1sen!2sin" 
                width="100%" 
                height="300" 
                style={{ border: 0, borderRadius: '8px' }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Full Name
                </label>
                <Input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Phone Number
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full"
                  placeholder="Enter your phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full"
                  placeholder="Enter your email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Matter Type
                </label>
                <Select 
                  value={formData.matterType} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, matterType: value }))}
                  required
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select practice area" />
                  </SelectTrigger>
                  <SelectContent>
                    {practiceAreasList.map((area, index) => (
                      <SelectItem key={index} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#1A1A1A' }}>
                  Brief Description
                </label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  className="w-full"
                  rows={5}
                  placeholder="Describe your legal matter"
                />
              </div>
              <Button
                type="submit"
                className="w-full text-base font-semibold py-6 transition-all duration-300 hover:opacity-90"
                style={{ backgroundColor: '#2B2BFF', color: 'white' }}
              >
                Send Inquiry
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;